import { useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Artikel, ArtikelFelder, Steuersatz, Unternehmen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload,
         useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../format.js";
import { MarkdownHinweis } from "./MarkdownHinweis.js";
// Lieferanten Strang E (Aufgabe 18)
import { KontierungFelder } from "./KontierungFelder.js";
import { KONTIERUNG_TEXT } from "./kontierungTexts.js";
import "./ArtikelDialog.css";

/**
 * Der Artikeldialog — EINE Komponente für die Verwaltung (Artikelstamm: anlegen UND
 * ändern) und den Artikelkatalog (ADMIN-Kurzweg: nur ändern). Herausgelöst aus
 * Verwaltung.tsx (04.09.), als der Katalog denselben Dialog brauchte: Ein zweiter,
 * nachgebauter Dialog wäre die sicherste Art, dass dasselbe Formular an zwei Bildschirmen
 * Verschiedenes bedeutet (Muster: PositionsEditor).
 *
 * Eigener CSS-Block `adlg` — mit KEINER Ansicht geteilt (Lektion aus `.ka__`, siehe
 * ArtikelDialog.css). Die Bedienlogik entspricht den übrigen Verwaltungsdialogen: kein
 * Versionsvergleich (last-write-wins), die Fachprüfung (Pflichtfelder, Längen, die
 * 20.000-Zeichen-Grenze der Beschreibung, Preisgrenze) macht der SERVER — hier steht nur,
 * was die Maske selbst wissen muss (Preis als Zahl, Steuersatz gewählt).
 *
 * Die Felder stehen ZWEISPALTIG (Wunsch vom 04.09.), die Beschreibung darunter über die
 * volle Breite als echtes, mehrzeiliges Textfeld — ein `<input>` schluckt Zeilenumbrüche.
 * Auf schmalen Bildschirmen fällt das Raster auf eine Spalte zurück (siehe CSS).
 *
 * WER den Feldsatz wohin schreibt, entscheidet der Aufrufer über `onSpeichern`: Die
 * Verwaltung legt an oder ändert, der Katalog ändert ausschliesslich — so braucht der
 * Katalog keinen Anlege-Weg in seinem Klienten, nur weil der Dialog einen kennt.
 */

export type ArtikelDialogSpeichern = {
  /** null = neuer Artikel (nur die Verwaltung öffnet den Dialog so). */
  id: string | null;
  /** Beim Anlegen die Wahl aus dem Dialog; beim Ändern unverändert der Bestand. */
  companyId: string;
  felder: ArtikelFelder;
};

export type ArtikelDialogProps = {
  /** null = neuen Artikel anlegen; sonst wird DIESER Artikel geändert. */
  artikel: Artikel | null;
  companies: Unternehmen[];
  /** Die bekannten Steuersätze — für die Auswahlliste (nur aktive) und die Vorbelegung. */
  steuersaetze: Steuersatz[];
  /** Vorbelegung beim Anlegen; beim Ändern kommen beide Werte aus `artikel`. */
  vorgabe: { companyId: string; taxRateId: string };
  /**
   * Schreibt den Feldsatz — wirft bei Ablehnung (der Dialog zeigt eine 422 an den
   * Feldern, alles andere als Sammelmeldung) und kehrt bei Erfolg still zurück.
   */
  onSpeichern: (eingabe: ArtikelDialogSpeichern) => Promise<void>;
  /** Nach ERFOLGREICHEM Speichern — der Aufrufer schliesst den Dialog und liest neu. */
  onGespeichert: () => void;
  /**
   * Der EINE Weg aus dem Panel neben dem Speichern: der Abbrechen-Knopf und die
   * Escape-Taste (Hausregel „striktes Modalpanel", 06.09.2026). Ein Klick auf den
   * Hintergrund ruft ihn NICHT mehr — er schliesst gar nicht.
   *
   * Die Rückfrage bei ungespeicherten Eingaben stellt der Dialog selbst, bevor er ihn
   * ruft: Nur er kennt seinen Entwurf. Der Aufrufer bekommt also einen Ruf, der schon
   * entschieden ist, und räumt bloss noch auf.
   */
  onAbbrechen: () => void;
  /** Lieferanten Strang E (Aufgabe 18): Erlöskonto und Kostenstelle des Artikels — nur die Artikelmaske der Verwaltung
   *  setzt sie; ohne sie fehlen die Felder und der Feldsatz trägt keine Kontierung (der Server behält die gespeicherte). */
  kontierung?: Pick<ApiClient, "getBookingAccounts" | "getCostCentres"> | undefined;
};

type Quelle = "" | "MONITOR" | "AIRCALL";

type Entwurf = {
  companyId: string;
  productNumber: string;
  name: string;
  description: string;
  unit: string;
  netUnitPrice: string;
  taxRateId: string;
  category: string;
  /** „Abrechnungsdaten aus" — "" heisst keine (Spec „Abrechnungsquellen"). */
  billingDataSource: Quelle;
  /** Die Kontierung (Lieferanten Strang E) — `null` heisst „ohne“. */
  bookingAccountId: string | null;
  costCentreId: string | null;
};

type FeldName = "productNumber" | "name" | "description" | "unit" | "netUnitPrice"
  | "taxRateId" | "category" | "billingDataSource" | "bookingAccountId" | "costCentreId";

const FELDNAMEN: readonly FeldName[] = ["productNumber", "name", "description", "unit",
  "netUnitPrice", "taxRateId", "category", "billingDataSource", "bookingAccountId", "costCentreId"];

/** Beschriftungen für die Sammelmeldung — nie der technische Feldname des Servers. */
const BESCHRIFTUNGEN: Readonly<Record<FeldName, string>> = {
  productNumber: "Artikelnummer", name: "Name", description: "Beschreibung", unit: "Einheit",
  netUnitPrice: "Nettopreis je Einheit", taxRateId: "Steuersatz", category: "Kategorie",
  billingDataSource: "Abrechnungsdaten aus",
  bookingAccountId: "Konto", costCentreId: "Kostenstelle",
};

/** Die drei Werte der Auswahl, in der Reihenfolge der Spec. */
const QUELLEN: readonly { wert: Quelle; text: string }[] = [
  { wert: "", text: "keine" }, { wert: "MONITOR", text: "Monitor" },
  { wert: "AIRCALL", text: "Aircall" },
];

const SATZ_FORMAT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

function entwurfAus(
  a: Artikel | null, vorgabe: { companyId: string; taxRateId: string },
): Entwurf {
  if (a === null) {
    return {
      companyId: vorgabe.companyId, productNumber: "", name: "", description: "",
      unit: "", netUnitPrice: "", taxRateId: vorgabe.taxRateId, category: "",
      billingDataSource: "", bookingAccountId: null, costCentreId: null,
    };
  }
  return {
    companyId: a.companyId, productNumber: a.productNumber, name: a.name,
    description: a.description ?? "", unit: a.unit, netUnitPrice: String(a.netUnitPrice),
    taxRateId: a.taxRateId, category: a.category ?? "",
    billingDataSource: a.billingDataSource ?? "",
    bookingAccountId: a.bookingAccountId, costCentreId: a.costCentreId,
  };
}

function entwurfGleich(a: Entwurf, b: Entwurf): boolean {
  return a.companyId === b.companyId && a.productNumber === b.productNumber
    && a.name === b.name && a.description === b.description && a.unit === b.unit
    && a.netUnitPrice === b.netUnitPrice && a.taxRateId === b.taxRateId
    && a.category === b.category && a.billingDataSource === b.billingDataSource
    && a.bookingAccountId === b.bookingAccountId && a.costCentreId === b.costCentreId;
}

// Bewusst benannte Doppelungen zu Verwaltung.tsx (dort für die übrigen Bereiche weiter
// gebraucht) — dieselbe Abwägung wie bei formatKonfliktZeit dort: Sie zusammenzulegen
// hiesse, einen gemeinsamen Formular-Hilfsbaustein zu bauen (nach @kundeninfo/ui
// gehörend), eine eigene Entscheidung samt Umbau aller Aufrufer.

const KEINE_ZAHL = Symbol("keine Zahl");

/** Text zu Zahl — leer bleibt leer, Komma zählt als Dezimaltrennzeichen. */
function alsZahl(eingabe: string): number | null | typeof KEINE_ZAHL {
  const roh = eingabe.trim();
  if (roh === "") return null;
  const zahl = Number(roh.replace(",", "."));
  return Number.isFinite(zahl) ? zahl : KEINE_ZAHL;
}

export function ArtikelDialog(p: ArtikelDialogProps): ReactElement {
  const [entwurf, setEntwurf] = useState<Entwurf>(() => entwurfAus(p.artikel, p.vorgabe));

  /**
   * Formularzustände (ME-Etappe 3, Aufgabe 6): `laeuft` sperrt Felder, Auswahlen, das
   * Beschreibungsfeld und den Fuß; eine 422 — auch die eigene „kein Preis"/„kein
   * Steuersatz" — hängt am Feld, der Fokus springt dorthin. Kein Versionsvergleich
   * (last-write-wins): eine 409 gibt es nicht.
   */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      await p.onSpeichern({
        id: p.artikel === null ? null : p.artikel.id,
        companyId: entwurf.companyId,
        felder: feldsatz(),
      });
      return true;
    },
    neuLaden: async () => { /* ohne Version kein fremder Stand */ },
    bekannteFelder: FELDNAMEN,
    beschriftungen: BESCHRIFTUNGEN,
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<FeldName, string>>;

  const geaendert = !entwurfGleich(entwurf, entwurfAus(p.artikel, p.vorgabe));
  useBeforeUnload(geaendert);

  /**
   * Abbrechen — mit Rückfrage, wenn etwas Ungespeichertes im Panel steht (Hausregel
   * „striktes Modalpanel", 06.09.2026; derselbe Wortlaut wie beim Verlassen-Wächter des
   * Routers, denn es ist derselbe Verlust). Escape geht denselben Weg, der Rahmen ruft
   * dieselbe Funktion.
   *
   * Der Riegel während des Schreibens liegt am Rahmen (`abbrechenGesperrt` unten) und gilt
   * damit für Knopf UND Taste; die Zeile hier ist die zweite Hälfte desselben Riegels für
   * den Fall, dass diese Funktion aus dem Inhalt heraus gerufen wird.
   */
  async function abbrechen(): Promise<void> {
    if (arbeitet) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  const neu = p.artikel === null;
  const titel = neu ? "Neuer Artikel" : "Artikel ändern";

  function setze<K extends keyof Entwurf>(feld: K, wert: Entwurf[K]): void {
    setEntwurf((e) => ({ ...e, [feld]: wert }));
  }

  /** Nur aktive Zeitscheiben stehen zur Auswahl — ein stillgelegter Satz soll sich nicht
   *  neu vergeben lassen. Eine bereits STILLGELEGTE Vorgabe bleibt sichtbar, damit das
   *  Ändern eines anderen Feldes sie nicht stillschweigend auf einen anderen Satz
   *  umstellt. */
  const waehlbareSaetze = p.steuersaetze.filter((s) => s.active);
  const angeboten = waehlbareSaetze.some((s) => s.id === entwurf.taxRateId)
    ? waehlbareSaetze
    : p.steuersaetze.filter((s) => s.active || s.id === entwurf.taxRateId);
  // Der Katalog kennt nur die AKTIVEN Sätze: Zeigt ein Artikel auf eine inzwischen
  // abgelöste Scheibe, hält diese Zeile den Bestand fest, statt ihn beim nächsten
  // Speichern still auf den ersten angebotenen Satz umzustellen.
  const vorgabeUnbekannt = entwurf.taxRateId !== ""
    && !angeboten.some((s) => s.id === entwurf.taxRateId);

  /** Der Feldsatz — wirft die eigene 422, wenn die Maske ihn selbst nicht lesen kann. */
  function feldsatz(): ArtikelFelder {
    const netUnitPrice = alsZahl(entwurf.netUnitPrice);
    if (netUnitPrice === KEINE_ZAHL || netUnitPrice === null) {
      throw new ApiError(422, "EINGABE_UNLESBAR", "Bitte einen Preis eintragen.",
        [{ field: "netUnitPrice", message: "Bitte einen Preis eintragen." }]);
    }
    if (entwurf.taxRateId === "") {
      throw new ApiError(422, "EINGABE_UNLESBAR", "Bitte einen Steuersatz wählen.",
        [{ field: "taxRateId", message: "Bitte einen Steuersatz wählen." }]);
    }
    // Die Fachprüfung (Pflichtfelder, Längen, Preisgrenze) macht der Server — hier steht
    // nur, was die Maske selbst wissen muss.
    return {
      productNumber: entwurf.productNumber,
      name: entwurf.name,
      description: entwurf.description.trim() === "" ? null : entwurf.description,
      unit: entwurf.unit,
      netUnitPrice,
      taxRateId: entwurf.taxRateId,
      category: entwurf.category.trim() === "" ? null : entwurf.category,
      // Die Maske schickt die Quelle IMMER mit — sie kennt das Feld (Ruling R-ARTIKEL gilt
      // für Aufrufer, die es nicht kennen).
      billingDataSource: entwurf.billingDataSource === "" ? null : entwurf.billingDataSource,
      // Die Kontierung nur, wenn die Maske sie zeigt — sonst behält der Server die gespeicherte (wie oben die Quelle).
      ...(p.kontierung === undefined ? {}
        : { bookingAccountId: entwurf.bookingAccountId, costCentreId: entwurf.costCentreId }),
    };
  }

  async function speichern(): Promise<void> {
    if (await formular.aktion() === true) p.onGespeichert();
  }

  return (
    /* Rahmen, Titel und Fußleiste kommen aus dem gemeinsamen Baustein (@kundeninfo/ui) —
       die Hausregel „striktes Modalpanel" steht dort an einer Stelle für alle Masken. Die
       Breite `mittel` ist die des zweispaltigen Feldrasters (Wunsch vom 04.09.). */
    <Modal titel={titel} onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}
      breite="mittel">
      {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}

      {/* Das Formular trägt die Zustände; das zweispaltige Raster (Wunsch vom 04.09.)
          bleibt das eigene `adlg__raster` darin — `Formular zweispaltig` ist für
          Beschriftung/Feld-PAARE gebaut, die Felder hier bringen ihre Beschriftung selbst
          mit (Formular beschriftung="oben"). */}
      <Formular beschriftung="oben" {...formular.formularProps}>
        <div className="adlg__raster">
          {neu ? (
            <Auswahl id="adlg-unternehmen" name="adlg-unternehmen" className="adlg__voll"
              beschriftung="Unternehmen des Artikels" wert={entwurf.companyId}
              /* Konto und Kostenstelle gehören zum Rahmen und den Kostenstellen DIESES Unternehmens — ein Wechsel
                 leert sie (Lieferanten Strang E, Fix-Runde 1, K3). */
              onGeaendert={(e) => setEntwurf((alt) => (alt.companyId === e.wert ? alt
                : { ...alt, companyId: e.wert, bookingAccountId: null, costCentreId: null }))}>
              {p.companies.map((c) => (
                <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
              ))}
            </Auswahl>
          ) : (
            // Beim Ändern nur noch anzeigen: Ein Artikel lässt sich nicht umhängen (seine
            // Nummer ist je Unternehmen eindeutig), der Server nähme das Feld gar nicht an.
            <div className="adlg__voll">
              <Feld id="adlg-unternehmen-fest" label="Unternehmen des Artikels"
                bearbeitbar={false}
                wert={p.companies.find((c) => c.id === entwurf.companyId)?.name ?? "—"}
                onChange={() => undefined} />
            </div>
          )}

          <Feld id="adlg-nummer" label="Artikelnummer" bearbeitbar
            wert={entwurf.productNumber}
            onChange={(w) => setze("productNumber", w)}
            {...(feldFehler.productNumber !== undefined
              ? { fehler: feldFehler.productNumber } : {})} />
          <Feld id="adlg-name" label="Name" bearbeitbar wert={entwurf.name}
            onChange={(w) => setze("name", w)}
            {...(feldFehler.name !== undefined ? { fehler: feldFehler.name } : {})} />
          <Feld id="adlg-einheit" label="Einheit" bearbeitbar wert={entwurf.unit}
            onChange={(w) => setze("unit", w)}
            {...(feldFehler.unit !== undefined ? { fehler: feldFehler.unit } : {})} />
          <Feld id="adlg-preis" label="Nettopreis je Einheit" bearbeitbar
            wert={entwurf.netUnitPrice}
            onChange={(w) => setze("netUnitPrice", w)}
            {...(feldFehler.netUnitPrice !== undefined
              ? { fehler: feldFehler.netUnitPrice } : {})} />
          <Auswahl id="adlg-steuer" name="adlg-steuer" beschriftung="Steuersatz"
            wert={entwurf.taxRateId}
            {...(feldFehler.taxRateId !== undefined ? { fehler: feldFehler.taxRateId } : {})}
            onGeaendert={(e) => setze("taxRateId", e.wert)}>
            {vorgabeUnbekannt && (
              <option value={entwurf.taxRateId}>bisheriger Steuersatz</option>
            )}
            {angeboten.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({SATZ_FORMAT.format(s.ratePercent)} %, ab {
                  formatDatum(s.validFrom)})
              </option>
            ))}
          </Auswahl>
          <Feld id="adlg-kategorie" label="Kategorie (optional)" bearbeitbar
            wert={entwurf.category}
            onChange={(w) => setze("category", w)}
            {...(feldFehler.category !== undefined
              ? { fehler: feldFehler.category } : {})} />

          {/* „Abrechnungsdaten aus" (Spec „Abrechnungsquellen") — unter der Kategorie. Die
              Position erbt die Quelle live über ihren Artikel; eine Übersteuerung je
              Position gibt es nicht (Entscheid 24.09.2026). */}
          <Auswahl id="adlg-quelle" name="adlg-quelle" beschriftung="Abrechnungsdaten aus"
            wert={entwurf.billingDataSource}
            {...(feldFehler.billingDataSource !== undefined
              ? { fehler: feldFehler.billingDataSource } : {})}
            onGeaendert={(e) => setze("billingDataSource", e.wert as Quelle)}>
            {QUELLEN.map((q) => (
              <option key={q.wert} value={q.wert}>{q.text}</option>
            ))}
          </Auswahl>

          {/* Lieferanten Strang E (Aufgabe 18): Erlöskonto und Kostenstelle — die Vorgabe der Auftragsposition. */}
          {p.kontierung !== undefined && (
            <div className="adlg__voll">
              <KontierungFelder point="PRODUCT" companyId={entwurf.companyId} client={p.kontierung}
                hint={KONTIERUNG_TEXT.productDefault}
                wert={{ bookingAccountId: entwurf.bookingAccountId, costCentreId: entwurf.costCentreId }}
                onWert={(w) => setEntwurf((e) => ({ ...e, ...w }))}
                fehler={{ ...(feldFehler.bookingAccountId === undefined ? {} : { bookingAccountId: feldFehler.bookingAccountId }),
                          ...(feldFehler.costCentreId === undefined ? {} : { costCentreId: feldFehler.costCentreId }) }} />
            </div>
          )}

          {/* Ganz unten über die volle Breite: Die Beschreibung ist der eine LANGE Wert
              (bis 20.000 Zeichen, siehe domain) — ein echtes, mehrzeiliges Textfeld:
              `Feld typ="mehrzeilig"` (me-eingabe). Seit me-design c9c0c08 behält eine
              bestehende Beschreibung ihre Zeilenumbrüche (Befund 2) — vorher ein rohes
              <textarea>. Befund (aria-describedby) und Sperre bringt das Element. */}
          <div className="adlg__auswahl adlg__voll">
            <Feld id="adlg-beschreibung" label="Beschreibung (optional)" typ="mehrzeilig"
              zeilen={6} bearbeitbar wert={entwurf.description}
              fehler={feldFehler.description}
              onChange={(wert) => setze("description", wert)} />
            {/* Die Beschreibung wird als Markdown gesetzt — in den Artikellisten (dort mit
                `kn-md-anriss` auf drei Zeilen geklemmt), im Detail und, sobald sie als
                Positionsbeschreibung in einen Beleg wandert, auch im PDF. */}
            <MarkdownHinweis />
          </div>
        </div>
      </Formular>

      <ModalFuss speichernText="Speichern"
        speichernGesperrt={formular.zustand !== "bereit"}
        abbrechenGesperrt={arbeitet}
        onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}
